import React from 'react'
import type { Feedback, FfpDashboardData, PeriodKey } from '../../types'
import { CHANNEL_WORDS, FEEDBACK_CHANNELS, formatPeople, formatPercent, periodRatingNote, plural } from '../../utils'
import type { FeedbackChannel } from '../../utils'
import { Card } from '../Card'
import { Message } from '../CardParts'
import { Stars } from '../Stars'
import styles from './feedback.module.sass'
import { ChannelDot, QuoteList } from './Quotes'
import {
  CommentsView,
  featuredQuotes,
  FeedbackMode,
  ratedChannels,
  ratedPeople,
  ratedReviews,
  reviewScope,
} from './utils'
import { WordCloud } from './WordCloud'

type Props = {
  data: FfpDashboardData
  period: PeriodKey
  mode: FeedbackMode
  view: CommentsView
  since: string
  highlighted: boolean
  onAbout: () => void
}

export const RatingsCard = ({ data, period, mode, view, since, highlighted, onAbout }: Props) => (
  <Card
    question="What your people say"
    title={ratingsTitle(data.feedback, mode, since)}
    onAbout={onAbout}
    highlighted={highlighted}
    footer={{ text: ratingsFooter(data, mode), chip: 'All feedback is anonymous' }}
  >
    <RatingsBody feedback={data.feedback} period={period} mode={mode} view={view} />
  </Card>
)

const ratingsTitle = (feedback: Feedback, mode: FeedbackMode, since: string): string => {
  if (mode !== 'ratings') return 'Ratings and comments'
  if ((feedback.average ?? 0) < 4) return `Ratings ${since}`

  const rated = ratedChannels(feedback)
  return rated.length === 1 ? `Your people rate ${CHANNEL_WORDS[rated[0]].name.toLowerCase()} highly` : 'Your people rate the programme highly'
}

const ratingsFooter = (data: FfpDashboardData, mode: FeedbackMode): string => {
  const { organization, feedback } = data
  if (mode === 'none') return `${organization.name} · No reviews yet`
  if (mode === 'hidden') return `${organization.name} · ${plural(feedback.reviews, 'review')} so far`
  return `${organization.name} · Since the start · n = ${plural(ratedReviews(feedback), 'review')} from ${formatPeople(ratedPeople(feedback))}`
}

type BodyProps = { feedback: Feedback; period: PeriodKey; mode: FeedbackMode; view: CommentsView }

const RatingsBody = ({ feedback, period, mode, view }: BodyProps) => {
  if (mode === 'none') {
    return (
      <>
        <p className={styles.metric}>From short surveys after coaching sessions and workshops</p>
        <Message title="No ratings yet" text="Ratings and comments will appear here once people rate their coaching sessions and workshops." />
      </>
    )
  }

  if (mode === 'hidden') {
    return (
      <>
        <p className={styles.metric}>{`${plural(feedback.reviews, 'review')} so far`}</p>
        <Message
          title={`Hidden for now · ${plural(feedback.reviews, 'review')} so far`}
          text={`Ratings and comments show once at least ${feedback.minimum_reviews} people have reviewed coaching, or ${feedback.minimum_reviews} have reviewed workshops, to protect anonymity.`}
        />
      </>
    )
  }

  return (
    <>
      <Headline feedback={feedback} />
      <PeriodNote feedback={feedback} period={period} />
      <ChannelTiles feedback={feedback} />
      <CommentsPreview feedback={feedback} view={view} />
    </>
  )
}

// "162 reviews of coaching and workshops · 91% rated 4 or 5 stars": the
// qualifying channels only, as the rating is. The tiles say why one is missing.
const ratingsMetric = (feedback: Feedback): string => {
  const reviews = `${ratedReviews(feedback)} ${reviewScope(ratedChannels(feedback))}`
  if (feedback.share_four_or_five === null) return reviews
  return `${reviews} · ${formatPercent(feedback.share_four_or_five, 0)} rated 4 or 5 stars`
}

const Headline = ({ feedback }: { feedback: Feedback }) => {
  const average = feedback.average ?? 0

  return (
    <div className={styles.headline}>
      <div className={styles.average}>
        <span className={styles.averageValue}>
          {average.toFixed(1)}
          <span className={styles.outOf}> / 5</span>
        </span>
        <Stars rating={average} size={22} />
      </div>
      <p className={styles.metric}>{ratingsMetric(feedback)}</p>
    </div>
  )
}

// The period had too few reviews to rate: say so, and keep the whole
// programme's rating in view.
const PeriodNote = ({ feedback, period }: { feedback: Feedback; period: PeriodKey }) => {
  const note = periodRatingNote(feedback, period)
  if (!note) return null

  return <Message title={note} text="Everything below covers the whole programme." />
}

const ChannelTiles = ({ feedback }: { feedback: Feedback }) => {
  const tiles = FEEDBACK_CHANNELS.map((channel) => (
    <ChannelTile key={channel} channel={channel} summary={feedback.channels[channel]} minimum={feedback.minimum_reviews} />
  ))
  return <div className={styles.tiles}>{tiles}</div>
}

type ChannelSummary = Feedback['channels'][FeedbackChannel]

const tileNote = ({ reviews, average }: ChannelSummary): string => {
  if (average !== null) return plural(reviews, 'review')
  return reviews === 0 ? 'Not in the overall rating yet' : 'Hidden to protect anonymity'
}

type TileProps = { channel: FeedbackChannel; summary: ChannelSummary; minimum: number }

const ChannelTile = ({ channel, summary, minimum }: TileProps) => (
  <div className={styles.tile}>
    <span className={styles.tileLabel}>
      <ChannelDot channel={channel} />
      {CHANNEL_WORDS[channel].name}
    </span>
    <TileRating summary={summary} minimum={minimum} />
    <span className={styles.tileNote}>{tileNote(summary)}</span>
  </div>
)

// A channel rates once enough different people have reviewed it, whatever
// its number of reviews.
const TileRating = ({ summary, minimum }: { summary: ChannelSummary; minimum: number }) => {
  if (summary.average === null) {
    return <span className={styles.tileEmpty}>{summary.reviews === 0 ? 'No reviews yet' : `Fewer than ${minimum} people have reviewed`}</span>
  }

  return (
    <span className={styles.tileRating}>
      <span className={styles.tileAverage}>{summary.average.toFixed(1)}</span>
      <Stars rating={summary.average} size={16} />
    </span>
  )
}

// Quotes around the cloud; quotes alone while there are few comments; a
// placeholder while every comment is still being prepared.
const CommentsPreview = ({ feedback, view }: { feedback: Feedback; view: CommentsView }) => {
  if (view === 'preparing') {
    return <p className={styles.preparing}>Comments are being prepared and will appear here shortly.</p>
  }

  const quotes = featuredQuotes(feedback.comments)
  if (view === 'quotes') {
    return (
      <div className={styles.quotesOnly}>
        <QuoteList quotes={quotes} layout="grid" />
        <p className={styles.smallPrint}>The word cloud appears once there are more written comments.</p>
      </div>
    )
  }

  return (
    <div className={styles.cloudBlock}>
      <div className={styles.cloudRow}>
        <QuoteList quotes={quotes.slice(0, 2)} layout="column" />
        <WordCloud keywords={feedback.keywords} />
        <QuoteList quotes={quotes.slice(2, 4)} layout="column" />
      </div>
      <QuoteList quotes={quotes.slice(4)} layout="grid" />
    </div>
  )
}
